/* HighSpark Wishlist v2 — first-party, no app, no dependency.
 *
 * Guests: the list lives in localStorage under hs_wishlist_v1 and never leaves
 * the browser. IF window.__hsWish.sync is true the list also syncs through the
 * Shopify App Proxy — for logged-in customers (signed customer id) AND for
 * guests (the pixel's anon id) — which signs every request
 * server-side; the browser never talks to the ERP origin directly.
 *
 * Store shape (v2):
 *   { v:2, items:[{h, v?, p?}, ...newest first], rm:[handle, ...] }
 *     h — product handle (the identity; the wishlist is product-level)
 *     v — variant id the shopper was looking at, as a STRING (Shopify ids can
 *         exceed 2^53; never store them as numbers)
 *     p — price in PAISE at save time (integer, Shopify's own unit)
 *   rm — tombstones removed locally but not yet confirmed by the server
 *
 * v1 stores ({v:1, items:["handle", ...]}) are migrated in place on read, so
 * an existing shopper keeps their list and simply has no variant/price on the
 * old entries until they re-save. NEVER drop an unrecognised store: a future
 * v3 reading this file must upgrade, not clear.
 *
 * Why v/p exist: back-in-stock and price-drop automations are variant-level
 * and price-threshold driven ("this exact variant is back", "cheaper than when
 * you saved it"). Capturing them at save time is the only moment the intent is
 * knowable.
 *
 * UI contracts (opt-in via data attributes, painted idempotently):
 *   [data-hs-wishlist-toggle][data-handle]  — any heart, anywhere.
 *       optional: data-variant, data-price (paise), data-prices (JSON map
 *       variantId→paise, used on the PDP where the picker changes the variant)
 *   [data-hs-wishlist-count]                — header bubble, hidden at 0
 *   [data-hs-wishlist-grid]                 — wishlist page grid
 *   [data-hs-wishlist-empty] / [data-hs-wishlist-error] — mutually exclusive
 *
 * QA hook: ?hs_wish_seed=h1,h2 — PREVIEW/EDITOR ONLY (see seedFromQuery).
 */
(function () {
  'use strict';

  var KEY = 'hs_wishlist_v1'; /* storage key is stable across schema versions */
  var MAX = 100;
  var FETCH_POOL = 6;
  var cfg = window.__hsWish || {};

  /* ── store ─────────────────────────────────────────────────────────────── */

  function migrate(d) {
    if (!d || typeof d !== 'object') return null;
    if (d.v === 2 && Array.isArray(d.items)) {
      if (!Array.isArray(d.rm)) d.rm = [];
      /* `add` = handles hearted since the last time we adopted the server's
         list. Only used for a signed-in shopper, whose sync sends CHANGES
         rather than the whole list (see pushSync). */
      if (!Array.isArray(d.add)) d.add = [];
      return d;
    }
    if (d.v === 1 && Array.isArray(d.items)) {
      /* v1 → v2: handles become items; no variant/price is knowable in
         retrospect, so they stay absent until the shopper re-saves. */
      return {
        v: 2,
        items: d.items.filter(function (h) { return typeof h === 'string' && h; })
                      .map(function (h) { return { h: h }; }),
        rm: Array.isArray(d.rm) ? d.rm : []
      };
    }
    return null;
  }

  function read() {
    try {
      var d = migrate(JSON.parse(localStorage.getItem(KEY)));
      if (d) return d;
    } catch (e) { /* corrupt or storage unavailable */ }
    return { v: 2, items: [], rm: [], add: [] };
  }
  function write(d) {
    try { localStorage.setItem(KEY, JSON.stringify(d)); } catch (e) { /* private mode / quota */ }
  }
  function handles() { return read().items.map(function (i) { return i.h; }); }
  function has(h) { return handles().indexOf(h) !== -1; }

  function add(h, meta) {
    if (!h) return;
    var d = read();
    var i = indexOf(d.items, h);
    if (i !== -1) {
      /* Already saved — refresh what we know without reordering the list. */
      if (meta && meta.v) d.items[i].v = meta.v;
      if (meta && meta.p != null) d.items[i].p = meta.p;
    } else {
      var it = { h: h };
      if (meta && meta.v) it.v = meta.v;
      if (meta && meta.p != null) it.p = meta.p;
      d.items.unshift(it);
      if (d.items.length > MAX) d.items.length = MAX;
    }
    var t = d.rm.indexOf(h);
    if (t !== -1) d.rm.splice(t, 1);
    if (d.add.indexOf(h) === -1) d.add.push(h);
    write(d);
    changed('add', h);
  }
  function indexOf(items, h) {
    for (var i = 0; i < items.length; i++) if (items[i].h === h) return i;
    return -1;
  }
  function remove(h) {
    var d = read();
    var i = indexOf(d.items, h);
    if (i === -1) return;
    d.items.splice(i, 1);
    if (d.rm.indexOf(h) === -1) d.rm.push(h);
    /* The server accepts at most 100 removals per sync; a long-offline phone
       must not build a list that 400s forever (audit 2026-10-23). */
    if (d.rm.length > 100) d.rm.splice(0, d.rm.length - 100);
    var a = d.add.indexOf(h);
    if (a !== -1) d.add.splice(a, 1);
    write(d);
    changed('remove', h);
  }
  function toggle(h, meta) {
    if (has(h)) { remove(h); return false; }
    add(h, meta);
    return true;
  }

  function changed(op, h) {
    revision++;
    paint();
    queueSync();
    try {
      document.dispatchEvent(new CustomEvent('hs:wishlist:change', { detail: { op: op, handle: h, handles: handles() } }));
    } catch (e) { /* ancient browser */ }
  }

  /* ── variant + price capture ───────────────────────────────────────────── */

  /* Where the numbers come from, in priority order:
     1. the button's own data-variant/data-price — the card case, where Liquid
        knew the product's selected variant at render time;
     2. data-prices (JSON map) + the LIVE variant id from the product form —
        the PDP case, where the picker may have changed the variant since
        render, so a static data-price would be stale;
     3. nothing — a legitimate state (e.g. the sticky bar on a page whose form
        hasn't hydrated); the server stores NULL and marketing treats it as
        "no threshold known". */
  function liveVariantId() {
    var el = document.querySelector('.product-variant-id, form[action*="/cart/add"] [name="id"]');
    var v = el && el.value;
    return /^\d{1,20}$/.test(v || '') ? String(v) : null;
  }
  function metaFor(btn) {
    var meta = {};
    var v = btn.getAttribute('data-variant');
    var p = btn.getAttribute('data-price');
    var map = btn.getAttribute('data-prices');
    if (map) {
      var live = liveVariantId();
      if (live) {
        meta.v = live;
        try {
          var parsed = JSON.parse(map);
          if (parsed && parsed[live] != null) meta.p = Number(parsed[live]);
        } catch (e) { /* malformed map — leave price unknown */ }
        return meta;
      }
    }
    if (/^\d{1,20}$/.test(v || '')) meta.v = String(v);
    if (p != null && p !== '' && isFinite(Number(p))) meta.p = Number(p);
    return meta;
  }

  /* ── painting ──────────────────────────────────────────────────────────── */

  function paint() {
    var hs = handles();
    var hearts = document.querySelectorAll('[data-hs-wishlist-toggle]');
    for (var i = 0; i < hearts.length; i++) {
      var b = hearts[i];
      var on = hs.indexOf(b.getAttribute('data-handle')) !== -1;
      b.classList.toggle('is-in', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      var label = b.getAttribute(on ? 'data-label-in' : 'data-label-out');
      /* Wishlist cards ship no data-label-*, so the heart had no accessible name
         at all. Fall back to something that states what the tap will do. */
      if (!label) label = on ? 'Remove from wishlist' : 'Save to wishlist';
      b.setAttribute('aria-label', label);
      b.setAttribute('title', label);
    }
    var counts = document.querySelectorAll('[data-hs-wishlist-count]');
    for (var j = 0; j < counts.length; j++) {
      var el = counts[j];
      var span = el.querySelector('[aria-hidden="true"]');
      if (span) span.textContent = hs.length > 99 ? '99+' : String(hs.length);
      /* Screen readers get words, not a bare numeral — Dawn's cart bubble
         carries the same pairing and the wishlist copy must match it. */
      var sr = el.querySelector('[data-hs-wishlist-count-sr]');
      if (sr) sr.textContent = hs.length === 1 ? '1 item in wishlist' : hs.length + ' items in wishlist';
      el.classList.toggle('hs-wish-count--none', hs.length === 0);
    }
  }

  /* One delegated listener serves every heart, including cards cloned by
     carousels after this script ran. stopPropagation keeps the tap from also
     reaching the card's stretched product link / third-party listeners. */
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest && e.target.closest('[data-hs-wishlist-toggle]');
    if (!b) return;
    e.preventDefault();
    e.stopPropagation();
    var h = b.getAttribute('data-handle');
    if (!h) return;


    /* Defence in depth against the class of bug fixed in facets.js today: if this
       button's PAINTED state disagrees with what is actually stored, then whatever
       injected it never repainted, and acting on the tap would destroy real data —
       toggle() reads storage, so a stale-empty heart over a saved product takes the
       remove() branch and tombstones it server-side too. Resync the paint and drop
       this one tap instead. Costs a single dead tap in a case that should never
       happen; the alternative is silent, unrecoverable data loss. */
    var painted = b.getAttribute('aria-pressed') === 'true';
    if (painted !== has(h)) { paint(); return; }

    var on = toggle(h, metaFor(b));
    if (on) {
      b.classList.remove('hs-wish-pop');
      void b.offsetWidth; /* restart the animation */
      b.classList.add('hs-wish-pop');
    } else {
      var item = b.closest('[data-hs-wish-item]');
      if (item) { item.remove(); settleStates(); }
    }
  }, true);

  /* Remove control on the wishlist page. Goes through the same remove() path as
     the heart, so the tombstone/sync behaviour is identical — no second way to
     mutate the list. */
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest && e.target.closest('[data-hs-wish-remove]');
    if (!b) return;
    e.preventDefault();
    var h = b.getAttribute('data-hs-wish-remove');
    if (!h) return;
    remove(h);
    var item = b.closest('[data-hs-wish-item]');
    if (item) { item.remove(); settleStates(); }
  });

  /* Exposed so dynamically appended cards can be repainted. Without this,
     infinite-scroll pages render saved products with an empty heart, and the
     next tap flips stored state the wrong way — silently UNSAVING the item. */
  window.hsWishlistPaint = paint;
  document.addEventListener('hs:wishlist:repaint', paint);

  window.addEventListener('storage', function (e) { if (e.key === KEY) paint(); });
  window.addEventListener('pageshow', paint);
  document.addEventListener('shopify:section:load', paint);
  window.addEventListener('load', paint);

  /* ── wishlist page ─────────────────────────────────────────────────────── */

  /* ── Sign in (KwikPass) and claim the guest list ────────────────────────── */

  /* Why this exists: a guest list belongs to a browser cookie, and only a small
     minority of anonymous browsers can ever be matched to a phone or email. A
     sign-in turns that browser into a VERIFIED PHONE NUMBER in one OTP step,
     which is the contactable identity price-drop alerts need. Claiming is what
     makes a saved wishlist reachable rather than merely stored.

     KwikPass (not Shopify's own email-OTP accounts) because it is phone-OTP,
     already live on this storefront, and — per GoKwik's integration guide — a
     successful OTP creates a real Shopify session, which is what makes the
     App Proxy start appending a SIGNED logged_in_customer_id. */

  var CLAIM_KEY = 'hs_wishlist_claim';

  /* The store's ACTUAL KwikPass entry point. An earlier version of this file
     looked for `__KP_LOGIN_SDK_INSTANCE__.handleKpLogin` — a global from
     GoKwik's HEADLESS integration guide that does not exist on a Shopify app
     install. It was always undefined, so the sign-in prompt never rendered:
     the fail-quiet design meant the feature was simply invisible rather than
     broken, which is why nothing errored. The real trigger is the one the
     header already uses (`hsDirectLogin` in theme.liquid). The headless global
     is kept as a fallback in case this store is ever migrated. */
  function kpLoginFn() {
    if (typeof window.handleKpAndShopifyLogin === 'function') {
      return function (returnTo) { window.handleKpAndShopifyLogin(returnTo || '/pages/wishlist'); };
    }
    if (window.__KP_LOGIN_SDK_INSTANCE__ &&
        typeof window.__KP_LOGIN_SDK_INSTANCE__.handleKpLogin === 'function') {
      return function () { window.__KP_LOGIN_SDK_INSTANCE__.handleKpLogin(); };
    }
    return null;
  }
  function kpAvailable() { return Boolean(kpLoginFn()); }

  /* Liquid's `customer` is not trustworthy on its own here — this theme says so
     itself in theme.liquid and ORs it with the KwikPass token, because a
     KwikPass sign-in does not necessarily re-render Liquid. Ask the same three
     sources, or the prompt nags people who are already signed in. */
  /* ── Two different "signed out" states (audit 2026-10-23) ───────────────
     DISPLACED: GoKwik keeps ONE session per number, so signing in on another
     phone kills this phone's session. The list here still belongs to that
     account, and its unsent edits must reach it after the next sign-in — keep
     everything, stop syncing, show the sign-in prompt.
     SIGNED OUT on purpose: the account's hearts must not stay on a shared
     phone or be copied into its guest list (and from there claimed into
     somebody else's account) — wipe them.
     The flag is set by whoever clears a refused session: this file and the
     account page. */
  var DISPLACED_KEY = 'hs_wishlist_displaced';
  var kpStale = false;   /* this page cleared a refused session; KwikPass memory is stale */
  function displaced() { try { return localStorage.getItem(DISPLACED_KEY) === '1'; } catch (e) { return false; } }
  function setDisplaced(on) { try { if (on) localStorage.setItem(DISPLACED_KEY, '1'); else localStorage.removeItem(DISPLACED_KEY); } catch (e) {} }
  function clearKpTokens() {
    /* KwikPass's own logout in QUIET mode (3rd arg true = no redirect): clears
       every key its sign-in check reads, including the "uni" token, without
       which Sign in redirects instead of opening the popup (2026-10-23). */
    try { if (typeof window.handleLogout === 'function') window.handleLogout(null, {}, true); } catch (e) {}
    ['KWIKSESSIONTOKEN', 'KWIKUSERTOKEN', 'acs_tkn'].forEach(function (k) {
      try { localStorage.removeItem(k); } catch (e) {}
      try { sessionStorage.removeItem(k); } catch (e) {}
      document.cookie = k + '=; Max-Age=0; path=/';
      document.cookie = k + '=; Max-Age=0; path=/; domain=.' + location.hostname.replace(/^www\./, '');
      document.cookie = k + '=; Max-Age=0; path=/; domain=' + location.hostname;
    });
  }

  function signedIn() {
    if (kpStale) return false;
    if (cfg.cid) return true;
    if (window.hsCustomerLoggedIn === true) return true;
    try { return Boolean(localStorage.getItem('KWIKSESSIONTOKEN')); } catch (e) { return false; }
  }

  /* Exposed for the wishlist page's sign-in button. Returns false when KwikPass
     hasn't loaded so the caller can leave the shopper alone rather than wire a
     button that silently does nothing. */
  window.hsWishlistLogin = function () {
    var fn = kpLoginFn();
    if (!fn) return false;
    /* Return them to the wishlist, not /account — they came here for the list.
       ⚠ If a STALE KwikPass token is still stored, KwikPass skips the OTP
       popup and just reloads this page, so the button "does nothing"
       (founder, iOS + Android, 2026-10-22). Check the session with GoKwik
       first; if GoKwik refuses it, clear the stale tokens so the popup opens. */
    /* This page already cleared a refused session: KwikPass's memory is stale,
       so reload first and open the popup after. */
    if (kpStale) {
      try { sessionStorage.setItem('hsWishOpenLogin', '1'); } catch (e) {}
      location.reload();
      return true;
    }
    var sess = null;
    try { sess = localStorage.getItem('KWIKSESSIONTOKEN'); } catch (e) {}
    if (!sess) {
      /* A dead session's "uni" token makes KwikPass redirect instead of opening
         the popup; a quiet KwikPass logout first (no redirect) prevents that. */
      try { if (typeof window.handleLogout === 'function') window.handleLogout(null, {}, true); } catch (e) {}
      try { fn('/pages/wishlist'); return true; } catch (e) { return false; }
    }
    mintKpToken().then(function (minted) {
      return (minted ? kpProfileRefused(minted) : Promise.resolve(false)).then(function (profRefused) { return { minted: minted, profRefused: profRefused }; });
    }).then(function (res) {
      var minted = res.minted;
      /* Only GoKwik refusing THIS session counts — a 401 mint, or a minted token
         it then refuses for the customer (401/404); 400/403/5xx/network never
         sign anybody out (audit 2026-10-23). */
      if ((!minted && kpMintStatus === 401) || res.profRefused) {
        clearKpTokens();
        setDisplaced(true);
        /* KwikPass keeps the stale "logged in" state in memory for this page
           load, so its popup would not open now. Reload, then open it. */
        try { sessionStorage.setItem('hsWishOpenLogin', '1'); } catch (e) {}
        location.reload();
        return;
      }
      try { fn('/pages/wishlist'); } catch (e) {}
    });
    return true;
  };

  function markPendingClaim() {
    var a = anonId();
    if (!a) return;
    try { localStorage.setItem(CLAIM_KEY, a); } catch (e) { /* private mode */ }
  }
  function clearPendingClaim() {
    try { localStorage.removeItem(CLAIM_KEY); } catch (e) {}
  }

  /* Idempotent server-side, so retrying is free. A 401 means the Shopify session
     KwikPass created is not visible to the App Proxy YET — keep the marker and
     try again on the next page load rather than dropping the list on the floor. */
  /* The KwikPass access token, read from the SAME three places the account
     page reads it (cookie first, then localStorage, then sessionStorage).

     ⚠ This is what makes the claim work on this store, and the reason is
     structural, not incidental. We are on the Shopify **Basic** plan, and
     KwikPass creates its Shopify session with **Multipass**, which is Plus
     only. Without a Shopify session the App Proxy never appends the signed
     `logged_in_customer_id`, so a claim authenticated purely on that parameter
     401s for every genuine shopper — which is exactly what happened: 0 of 453
     wishlist rows had ever been claimed.

     The token is not an identity claim. The server exchanges it with GoKwik
     and believes only GoKwik's answer, the same way /api/account/orders has
     always worked. Sending it here costs nothing when a proxy id is present,
     because the server prefers the proxy id. */
  function kpToken() {
    var keys = ['acs_tkn', 'KWIKSESSIONTOKEN', 'KWIKUSERTOKEN'];
    for (var i = 0; i < keys.length; i++) {
      var n = keys[i];
      try {
        var m = (document.cookie || '').match(new RegExp('(?:^|; )' + n + '=([^;]*)'));
        if (m) return decodeURIComponent(m[1]);
      } catch (e) {}
      try { var l = localStorage.getItem(n); if (l) return l; } catch (e) {}
      try { var ss = sessionStorage.getItem(n); if (ss) return ss; } catch (e) {}
    }
    return null;
  }

  /* ⚠ A STORED KwikPass token is not an access token. GoKwik answers
     /customer/profile with 401 "Authentication failed" for every one of the
     three stored values (measured 2026-10-22, founder's own session), so the
     claim below 401'd forever and a list saved on one phone never reached
     the other. The account page has always worked because it first MINTS an
     access token from KWIKSESSIONTOKEN — the same call the KwikPass plugin
     makes. Do the same here; fall back to the stored value only if minting
     is impossible, so nothing is worse than before. */
  var KP_MID = '191lakiven63';
  var kpMintStatus = 0;   /* HTTP status of the last mint; 0 = network error / not tried */
  function mintKpToken() {
    var sess = null;
    try { sess = localStorage.getItem('KWIKSESSIONTOKEN'); } catch (e) {}
    if (!sess || !window.fetch) return Promise.resolve(null);
    return fetch('https://gkx.gokwik.co/kp/api/v1/auth/core-token/login', {
      headers: { 'kp-merchant-id': KP_MID, 'gk-merchant-id': KP_MID, 'kp-request-id': 'kwikPass_plugin', 'token': sess }
    })
      .then(function (r) { kpMintStatus = r.status; return r.ok ? r.json() : null; })
      .then(function (j) { return (j && j.data && j.data.token) || (j && j.token) || null; })
      .catch(function () { kpMintStatus = 0; return null; });
  }

  /* The Shopify customer access token KwikPass stores as `acs_tkn`. GoKwik's own
     client sends it next to the access token, and so does the account page;
     the server forwards it to GoKwik when verifying (2026-10-23). */
  function acsVal() {
    try {
      var m = (document.cookie || '').match(/(?:^|; )acs_tkn=([^;]*)/);
      if (m) return decodeURIComponent(m[1]);
    } catch (e) {}
    try { return localStorage.getItem('acs_tkn') || null; } catch (e) { return null; }
  }
  /* ⚠ A 200 mint does NOT prove the session is alive. Measured 2026-10-23 on a
     device displaced by a sign-in elsewhere: GoKwik minted a token, then
     answered 401/404 to every customer call made with it. Resolves true only
     when GoKwik itself refuses (401/404); network errors and 5xx resolve false. */
  function kpProfileRefused(tok) {
    if (!tok || !window.fetch) return Promise.resolve(false);
    var h = { 'gk-access-token': tok, 'gk-merchant-id': KP_MID, 'gk-platform': 'shopify',
              'kp-integration-type': 'PLUGIN', 'kp-request-id': 'kwikPass_plugin' };
    var acs = acsVal(); if (acs) h['X-Shopify-Customer-Access-Token'] = acs;
    return fetch('https://gkx.gokwik.co/kp/api/v1/customer/profile', { headers: h })
      .then(function (r) { return r.status === 401 || r.status === 404; }, function () { return false; });
  }
  function markDisplaced() {
    clearKpTokens();
    setDisplaced(true);
    kpStale = true;
    kpAccessP = null;
    paintSignin();
  }

  function attemptClaim() {
    if (!cfg.sync || !cfg.proxy) return;
    var a;
    try { a = localStorage.getItem(CLAIM_KEY); } catch (e) { return; }
    if (!a) return;
    mintKpToken().then(function (minted) { sendClaim(a, minted); });
  }

  function sendClaim(a, minted) {
    var headers = { 'Content-Type': 'application/json' };
    var tok = minted || kpToken();
    if (tok) headers['gk-access-token'] = tok;
    var acsC = acsVal(); if (tok && acsC) headers['x-shopify-customer-access-token'] = acsC;

    fetch(cfg.proxy + '/claim', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ anonId: a })
    })
      .then(function (r) {
        if (r.status === 401) {
          /* ⚠ Do NOT let this be silent. Treating 401 as "retry later" is
             correct behaviour and was also the reason a 100% failure rate went
             unnoticed for a month: the marker persists, the retry happens, and
             nothing anywhere records that it never once succeeded. Emit a
             breadcrumb the pixel can pick up so a permanently-failing claim is
             visible without opening a console. */
          try {
            document.dispatchEvent(new CustomEvent('hs:wishlist:claim-unauthenticated', {
              detail: { hadToken: Boolean(tok) }
            }));
          } catch (e) {}
          return null;                              /* session not visible yet — retry later */
        }
        if (!r.ok) { clearPendingClaim(); return null; }  /* 4xx that retrying won't fix */
        return r.json();
      })
      .then(function (j) {
        if (!j || j.status !== 'ok') return;
        clearPendingClaim();
        /* Adopt the canonical post-merge list so the hearts match the account
           immediately, without waiting for a reload. */
        var d = read();
        /* ⚠ Do not drop what the server has not seen yet (red-team 2026-10-23):
           a heart tapped just before sign-in and a removal made while this was
           in flight. Server list first, then local pending adds, minus local
           pending removals; both stay pending so the next sync sends them. */
        var removedNow = {};
        d.rm.forEach(function (h) { removedNow[h] = true; });
        var serverItems = (j.items || []).map(function (i) {
          var o = { h: i.handle };
          if (i.variantId) o.v = i.variantId;
          if (i.pricePaise != null) o.p = i.pricePaise;
          return o;
        });
        var have = {};
        serverItems.forEach(function (it) { have[it.h] = true; });
        var pendingAdds = d.items.filter(function (it) { return d.add.indexOf(it.h) !== -1 && !have[it.h]; });
        /* ⚠ A DIFFERENT account just signed in on this browser (audit 2026-10-23):
           the pending edits belong to the previous account — drop them, never send
           them as this account's. Show this account's list as the server has it. */
        var prevOwner = storedOwner();
        if (j.owner === 'customer' && j.ownerKey && prevOwner && prevOwner !== j.ownerKey) {
          d.add = []; d.rm = [];
          d.items = serverItems.slice(0, MAX);
        } else {
          d.items = pendingAdds.concat(serverItems).filter(function (it) { return !removedNow[it.h]; }).slice(0, MAX);
        }
        write(d);
        /* The claim tells us whose list this is, so the next sync can send only
           changes and is covered by the account-switch fence. */
        if (j.owner === 'customer' && j.ownerKey) { setOwner(j.ownerKey); setDisplaced(false); }
        revision++;
        paint();
        try {
          document.dispatchEvent(new CustomEvent('hs:wishlist:claimed', {
            detail: { claimed: j.claimed || 0, merged: j.merged || 0 }
          }));
        } catch (e) {}
      })
      .catch(function () { /* keep the marker; next load retries */ });
  }

  /* KwikPass dispatches this on the WINDOW after a successful OTP. We do not
     read anything out of event.detail — it carries phone/email/shopifyCustomerId
     but the server must take identity from the SIGNED proxy parameter, never
     from a value the page could fabricate. The event is used purely as a
     "something changed, try the claim now" signal. */
  window.addEventListener('kp_data_sent', function () {
    markPendingClaim();
    attemptClaim();
  });

  /* Reveal the sign-in prompt only when it can actually do something: the
     shopper is signed out AND the KwikPass SDK has loaded. The markup ships
     hidden, so if KwikPass never loads the shopper simply never sees a button —
     which is the correct failure mode for an optional prompt. The SDK is
     third-party and can arrive after us, so we re-check a few times rather than
     deciding once at load and being permanently wrong. */
  /* Sign-in prompt measurement (PR #1008, merged to the repo but never pushed live
     until 2026-10-23). hs-core2.js already listens for `hs:wishlist:signin`. */
  var signinShownAnnounced = false;
  function paintSignin() {
    var box = document.querySelector('[data-hs-wishlist-signin]');
    if (!box) return false;
    var show = !signedIn() && kpAvailable();
    box.hidden = !show;
    /* The IMPRESSION once per page — paintSignin is polled while KwikPass loads. */
    if (show && !signinShownAnnounced) {
      signinShownAnnounced = true;
      announceSignin('shown');
    }
    return show;
  }

  /* Announced, never sent from here: this file owns the wishlist, hs-core2.js the pixel. */
  function announceSignin(phase) {
    try {
      document.dispatchEvent(new CustomEvent('hs:wishlist:signin', { detail: { phase: phase } }));
    } catch (e) { /* ancient browser */ }
  }

  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest && e.target.closest('[data-hs-wishlist-signin-btn]');
    if (!b) return;
    e.preventDefault();
    /* Announced BEFORE handing off to KwikPass, which can navigate. */
    announceSignin('click');
    /* Before opening the OTP sheet, remember which guest list this browser owns
       — after login the claim needs it, and a reload in between would otherwise
       lose the connection between the two identities. */
    markPendingClaim();
    if (!window.hsWishlistLogin()) {
      /* SDK vanished between paint and tap: hide rather than leave a dead button. */
      var box = document.querySelector('[data-hs-wishlist-signin]');
      if (box) box.hidden = true;
    }
  });

  /* Once the list belongs to an account there is nothing left to ask for. */
  document.addEventListener('hs:wishlist:claimed', function () {
    var box = document.querySelector('[data-hs-wishlist-signin]');
    if (box) box.hidden = true;
  });

  if (document.querySelector('[data-hs-wishlist-signin]')) {
    var signinTries = 0;
    (function pollForKp() {
      if (paintSignin() || signinTries++ >= 10) return;  /* ~5s, then give up quietly */
      setTimeout(pollForKp, 500);
    })();
  }

  function grid() { return document.querySelector('[data-hs-wishlist-grid]'); }
  function el(sel) { return document.querySelector(sel); }

  /* Three distinct outcomes, never conflated:
       saved 0                      → empty state ("nothing saved yet")
       saved >0, some cards drawn   → the grid
       saved >0, ZERO cards drawn   → error state ("couldn't load, retry")
     Deciding from the DOM alone made a failed render look identical to an
     empty wishlist, telling shoppers their saved pieces were gone. */
  function settleStates(failed) {
    var g = grid();
    if (!g) return;
    var saved = handles().length;
    var drawn = g.querySelectorAll('[data-hs-wish-item]').length;
    var isEmpty = saved === 0;
    var isError = !isEmpty && drawn === 0 && failed === true;
    /* PARTIAL: some cards rendered and some did not. Previously this was
       silent — the header bubble counted every saved handle while the grid
       showed only what loaded, so a shopper with 8 saved and 2 rendered saw
       "8" and two products and reasonably concluded the count was wrong. The
       count is right; the grid was incomplete. Say so, and offer the retry
       that already exists rather than pruning saves we have no evidence are
       actually gone. */
    var isPartial = !isEmpty && !isError && drawn > 0 && drawn < saved;
    g.hidden = drawn === 0;
    var e1 = el('[data-hs-wishlist-empty]'); if (e1) e1.hidden = !isEmpty;
    var e2 = el('[data-hs-wishlist-error]');
    if (e2) {
      e2.hidden = !(isError || isPartial);
      var t = e2.querySelector('.hs-wish-empty-title');
      var x = e2.querySelector('.hs-wish-empty-text');
      if (isPartial) {
        var miss = saved - drawn;
        if (t) t.textContent = 'Showing ' + drawn + ' of ' + saved + ' saved';
        if (x) x.textContent = miss === 1
          ? '1 item could not be loaded just now. It is still saved — try again.'
          : miss + ' items could not be loaded just now. They are still saved — try again.';
      } else if (isError) {
        /* Restore the section's own copy for the total-failure case. */
        if (t && t.getAttribute('data-hs-default')) t.textContent = t.getAttribute('data-hs-default');
        if (x && x.getAttribute('data-hs-default')) x.textContent = x.getAttribute('data-hs-default');
      }
    }
  }

  /* Bounded concurrency: the cap is 100 saved items and Shopify throttles
     bursts, so fire at most FETCH_POOL section requests at a time. */
  function pooled(list, worker, limit) {
    return new Promise(function (resolve) {
      var out = new Array(list.length), idx = 0, active = 0, done = 0;
      if (!list.length) return resolve(out);
      function next() {
        while (active < limit && idx < list.length) {
          (function (i) {
            active++; idx++;
            worker(list[i]).then(function (r) { out[i] = r; })
              .catch(function () { out[i] = null; })
              .then(function () {
                active--; done++;
                if (done === list.length) resolve(out); else next();
              });
          })(idx);
        }
      }
      next();
    });
  }

  /* A 404 means the product is genuinely gone and the handle is pruned. Anything
     else (429 throttling, a 5xx, a dropped connection) is TRANSIENT: the handle
     is kept, because deleting a real save over a blip is unrecoverable.
     One retry, because the page fires FETCH_POOL requests at once and Shopify
     throttles bursts — without it a throttled item just silently vanishes from
     the grid while still being counted, which is exactly the "8 saved, 2 shown"
     confusion this fixes. */
  function fetchCardOnce(h) {
    return fetch('/products/' + encodeURIComponent(h) + '?sections=hs-wishlist-card')
      .then(function (r) {
        if (r.status === 404) return { gone: true, h: h };
        if (!r.ok) return null;
        return r.json().then(function (j) { return { html: j['hs-wishlist-card'] || '', h: h }; });
      })
      .catch(function () { return null; });
  }
  function fetchCard(h) {
    return fetchCardOnce(h).then(function (r) {
      if (r) return r;
      return new Promise(function (res) { setTimeout(res, 600); }).then(function () {
        return fetchCardOnce(h);
      });
    });
  }

  function renderPage() {
    var g = grid();
    if (!g) return;
    /* Snapshot the merchant-authored error copy once, so switching to the
       partial message and back cannot permanently overwrite it. */
    var eb = el('[data-hs-wishlist-error]');
    if (eb) {
      var t0 = eb.querySelector('.hs-wish-empty-title');
      var x0 = eb.querySelector('.hs-wish-empty-text');
      if (t0 && !t0.getAttribute('data-hs-default')) t0.setAttribute('data-hs-default', t0.textContent);
      if (x0 && !x0.getAttribute('data-hs-default')) x0.setAttribute('data-hs-default', x0.textContent);
    }
    var hs = handles();
    if (!hs.length) { settleStates(false); return; }

    g.hidden = false;
    var e2 = el('[data-hs-wishlist-error]'); if (e2) e2.hidden = true;
    var skel = '';
    for (var i = 0; i < hs.length; i++) skel += '<div class="hs-wish-skel"></div>';
    g.innerHTML = skel;

    pooled(hs, fetchCard, FETCH_POOL).then(function (results) {
      g.innerHTML = '';
      var failures = 0;
      for (var i = 0; i < results.length; i++) {
        var r = results[i];
        if (!r) { failures++; continue; }
        if (r.gone) { remove(r.h); continue; } /* product deleted/unpublished */
        if (!r.html || r.html.indexOf('hs-card-global') === -1) { failures++; continue; }
        var w = document.createElement('div');
        w.setAttribute('data-hs-wish-item', r.h);
        w.innerHTML = r.html;
        /* Explicit remove. The heart on the card already toggles, but it carries
           no label and there is no hover on touch, so on a page of saved items
           nothing indicated how to unsave one. Added here rather than in the
           card section because the card is shared with other surfaces where a
           "Remove" control would make no sense. */
        var rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'hs-wish-remove';
        rm.setAttribute('data-hs-wish-remove', r.h);
        rm.textContent = 'Remove';
        w.appendChild(rm);
        g.appendChild(w);
      }
      settleStates(failures > 0);
      paint();
    });
  }

  /* ── App-Proxy sync (logged-in AND guest) ─────────────────────────────── */

  /* Guests sync too, keyed on the first-party pixel's anon id.
     Why: keying only on a signed logged_in_customer_id measured SIGN-IN RATE,
     not wishlist usage — Shopify's new customer accounts sign in by email OTP
     and almost nobody does, so the server table held 3 rows from 1 customer
     while the storefront served ~31k people a month. Price-drop alerts read
     that table, so with guests excluded there was essentially nobody to alert.

     The anon id identifies a BROWSER, not a person. It grants nothing: it can
     only read and write its own list of product handles. The App-Proxy HMAC
     still proves the request came via the storefront (it signs the query
     string), which is the same footing the pixel already runs on. */
  function anonId() {
    /* Written by hs-core2.js on every page. Read-only here — the wishlist must
       never MINT one, or a browser with tracking blocked would get a fresh id
       per page load and scatter one-item lists across the table. */
    try { return localStorage.getItem('_hs_anon') || null; } catch (e) { return null; }
  }
  /* Do we have ANY identifier worth syncing with? The choice of owner is made
     SERVER-side: a Shopify-signed logged_in_customer_id always beats a
     body-supplied anonId. We therefore send the anon id whenever we have one,
     even while logged in — that costs nothing and closes the window right after
     a KwikPass login where the Shopify session exists but the App Proxy has not
     surfaced it yet. Sending only one or the other would drop syncs in exactly
     that window. */
  function canSync() { return Boolean(cfg.cid || anonId()); }

  /* Protocol: push the full local list plus tombstones; the server merges
     (union minus removes), returns the canonical list; we adopt it and clear
     the tombstones.
     ⚠ At most ONE sync in flight per tab. The server is idempotent per
     request but NOT commutative across reordered requests: an add and a
     later remove that land out of order resurrect the removed item. The
     guard below is what makes that impossible in the common (single-tab)
     case — do not remove it. A sync requested while one is in flight sets
     `pending` and runs once the current one settles. */
  var syncTimer = null, inFlight = false, pending = false;
  /* Bumped by every local add/remove. Captured before a sync leaves and
     compared when it returns — see the merge in pushSync. */
  var revision = 0;

  function queueSync() {
    if (!cfg.sync || !cfg.proxy || !canSync()) return;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(pushSync, 800);
  }
  /* ── Cross-device sync for a signed-in shopper (2026-10-23) ─────────────
     The server now resolves a KwikPass token to the CUSTOMER on /sync, so
     every signed-in phone reads and writes ONE list. That changes what may
     be sent:
       • guest (or a server that does not answer owner:"customer"): the old
         protocol — push the whole local list plus tombstones.
       • signed in, and this browser has already merged into THIS account
         (OWNER_KEY matches): push only what changed here — hearts added
         since the last adopt (`d.add`) and removals (`d.rm`). Pushing the
         whole list would RESURRECT a heart removed on another phone, because
         this phone still holds it locally.
       • signed in, first time on this browser: push the whole list once, so
         anything saved before signing in joins the account; then adopt.
     ⚠ Delta mode is entered only on the server's word (owner:"customer"). If
     a delta push comes back as a guest list (token rejected), nothing is
     adopted and the next sync goes back to the full list. */
  var OWNER_KEY = 'hs_wishlist_owner';
  var kpAccessP = null;
  function kpAccess() {
    var sess = null;
    try { sess = localStorage.getItem('KWIKSESSIONTOKEN'); } catch (e) {}
    if (!sess) { kpAccessP = null; return Promise.resolve(null); }
    /* A failed mint is not remembered: the next sync tries again. */
    if (!kpAccessP) kpAccessP = mintKpToken().then(function (t) { if (!t) kpAccessP = null; return t; });
    return kpAccessP;
  }
  function listFrom(items) {
    return (items || []).slice(0, MAX).map(function (i) {
      var o = { h: i.handle };
      if (i.variantId) o.v = String(i.variantId);
      if (i.pricePaise != null) o.p = Number(i.pricePaise);
      return o;
    });
  }
  function storedOwner() { try { return localStorage.getItem(OWNER_KEY) || null; } catch (e) { return null; } }
  function setOwner(k) { try { if (k) localStorage.setItem(OWNER_KEY, k); else localStorage.removeItem(OWNER_KEY); } catch (e) {} }

  function pushSync() {
    if (!cfg.sync || !cfg.proxy || !canSync()) return;
    if (inFlight) { pending = true; return; }
    inFlight = true;
    kpAccess().then(function (tok) { sendSync(tok); }, function () { sendSync(null); });
  }
  function sendSync(tok) {
    var d = read();
    var sentAtRevision = revision;
    var session = null;
    try { session = localStorage.getItem('KWIKSESSIONTOKEN'); } catch (e) {}
    /* ⚠ Signed in but the mint failed (GoKwik blip or an expired session):
       do NOT fall back to a guest sync, and do NOT forget the account. Either
       would make the next sync re-send this phone's whole list and bring back
       hearts removed on another phone (red-team 2026-10-23). The edits stay
       in d.add / d.rm and go out on the next successful sync. */
    if (!tok && session) {
      if (kpMintStatus === 401) {
        /* Refused, not a blip: this phone was signed out (usually by the same
           number signing in on another phone). Keep the list and its edits,
           show the sign-in prompt. */
        clearKpTokens();
        setDisplaced(true);
        kpStale = true;
        paintSignin();
      }
      inFlight = false; return;
    }
    var key = storedOwner();
    if (!tok && key) {
      if (displaced()) { inFlight = false; return; }   /* wait for the re-sign-in */
      /* Signed out on purpose: forget this account's hearts on this browser. */
      write({ v: 2, items: [], rm: [], add: [] });
      setOwner(null);
      paint();
      if (grid()) renderPage();
      inFlight = false; return;
    }
    if (tok) setDisplaced(false);
    /* `key` is never cleared on sign-out: it means "this browser has already
       merged into an account". After that, a sign-in sends CHANGES only. */
    var delta = Boolean(tok && key);
    var sentAdds = d.add.slice(), sentRm = d.rm.slice();
    var src = delta
      ? d.items.filter(function (i) { return sentAdds.indexOf(i.h) !== -1; })
      : d.items;
    var headers = { 'Content-Type': 'application/json' };
    if (tok) headers['gk-access-token'] = tok;
    var acsS = acsVal(); if (tok && acsS) headers['x-shopify-customer-access-token'] = acsS;
    /* Which account these changes belong to. A different account signed in
       on this browser makes the server answer 409 and write nothing. */
    if (delta) headers['x-hs-wishlist-owner'] = key;
    fetch(cfg.proxy + '/sync', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        /* Only sent for guests. When Shopify signs a logged_in_customer_id the
           server ignores the body value entirely — a body-supplied id must
           never be able to redirect a signed-in shopper's list. */
        anonId: anonId() || undefined,
        items: src.map(function (i) {
          var o = { handle: i.h };
          if (i.v) o.variantId = String(i.v);
          if (i.p != null) o.pricePaise = Number(i.p);
          return o;
        }),
        remove: sentRm
      })
    })
      .then(function (r) { return (r.ok || r.status === 409) ? r.json() : null; })
      .then(function (j) {
        if (!j || !Array.isArray(j.items)) return;
        var customer = j.owner === 'customer' && j.ownerKey;
        if (j.ownerMismatch && customer) {
          /* Another account is signed in here now. The pending edits belonged
             to the previous one and were NOT written; show this account's list. */
          setOwner(j.ownerKey);
          write({ v: 2, items: listFrom(j.items), rm: [], add: [] });
          paint(); if (grid()) renderPage();
          return;
        }
        if (customer) {
          setOwner(j.ownerKey);
        } else if (tok && j.owner === 'anon') {
          /* The server could not verify this token. If GoKwik itself refuses
             the session, this phone was signed out (usually by the same number
             signing in elsewhere): keep the list, show Sign in. */
          kpProfileRefused(tok).then(function (refused) { if (refused) markDisplaced(); });
          if (delta) { kpAccessP = null; return; }
        } else if (delta) {
          /* A CHANGES-only push answered as a guest list (token refused). Do
             not adopt that partial list; keep the edits and re-mint next time. */
          kpAccessP = null;
          return;
        }
        /* Guest reply on a browser that has merged before: keep the pending
           edits, they still have to reach the account on the next sign-in. */
        var keepEdits = !customer && Boolean(key);
        var canonical = listFrom(j.items);

        /* ⚠ NEVER adopt the server list blindly. The shopper can tap a heart
           while this request is in flight (a window of network latency, not
           of the 800ms debounce), and an unconditional write() silently threw
           that tap away — from localStorage AND from the server, because the
           follow-up sync then read the already-clobbered store. That is a
           saved item vanishing in front of the customer.
           If nothing changed locally, adopt the canonical list as-is.
           If something did, replay those local edits ON TOP of it and sync
           again, so the server converges to what the shopper actually did. */
        if (revision === sentAtRevision) {
          if (keepEdits) {
            var cur = read();
            write({ v: 2, items: canonical, rm: cur.rm, add: cur.add });
          } else {
            write({ v: 2, items: canonical, rm: [], add: [] });
          }
        } else {
          var local = read();
          var removed = {};
          local.rm.forEach(function (h) { removed[h] = true; });
          var merged = [];
          var seen = {};
          /* Local first: newest-first order and any variant/price the shopper
             just captured win over the server's older copy. */
          local.items.forEach(function (it) {
            if (removed[it.h] || seen[it.h]) return;
            seen[it.h] = true; merged.push(it);
          });
          canonical.forEach(function (it) {
            if (removed[it.h] || seen[it.h]) return;
            seen[it.h] = true; merged.push(it);
          });
          /* Keep only the edits the server has NOT seen yet. */
          write({ v: 2, items: merged.slice(0, MAX),
                  rm: keepEdits ? local.rm : local.rm.filter(function (h) { return sentRm.indexOf(h) === -1; }),
                  add: keepEdits ? local.add : local.add.filter(function (h) { return sentAdds.indexOf(h) === -1; }) });
          pending = true; /* re-sync so the server learns the in-flight edits */
        }
        paint();
        if (grid()) renderPage();
      })
      .catch(function () { /* offline / proxy down — local copy still rules */ })
      .then(function () {
        inFlight = false;
        if (pending) { pending = false; queueSync(); }
      });
  }

  /* ── QA seed ───────────────────────────────────────────────────────────── */

  /* PREVIEW/EDITOR ONLY. On the published theme a crafted link must not be
     able to write into a shopper's list — with sync live that pollution would
     follow a logged-in customer across devices. Gate on the theme's actual
     ROLE, not on the mere presence of a preview_theme_id param (which anyone
     can append to any URL). */
  function seedAllowed() {
    var S = window.Shopify || {};
    if (S.designMode) return true;
    return !!(S.theme && S.theme.role && String(S.theme.role).toLowerCase() !== 'main');
  }
  function seedFromQuery() {
    try {
      var m = new URLSearchParams(location.search).get('hs_wish_seed');
      if (!m || !seedAllowed()) return;
      m.split(',').forEach(function (h) { h = h.trim(); if (h) add(h); });
    } catch (e) { /* no URLSearchParams */ }
  }

  window.HSWishlist = {
    list: handles, has: has, add: add, remove: remove, toggle: toggle,
    items: function () { return read().items; }
  };

  function init() {
    seedFromQuery();
    paint();
    renderPage();
    if (cfg.sync && cfg.proxy && canSync()) pushSync();
    attemptClaim();
    /* Second half of the stale-session recovery in hsWishlistLogin: the page
       reloaded so KwikPass booted signed-out; finish what the tap asked for. */
    var reopen = false;
    try { reopen = sessionStorage.getItem('hsWishOpenLogin') === '1'; sessionStorage.removeItem('hsWishOpenLogin'); } catch (e) {}
    if (reopen) {
      /* ⚠ KwikPass defines its login function BEFORE its popup is ready; a
         call at that moment is silently dropped (measured on staging
         2026-10-23). So retry until the popup frame actually has a size. */
      var tries = 0, iv = setInterval(function () {
        tries++;
        var open = Array.prototype.some.call(document.querySelectorAll('iframe[src*="kwikpass"]'), function (f) {
          return f.getBoundingClientRect().height > 100;
        });
        if (open || tries > 12) { clearInterval(iv); return; }
        var fn = kpLoginFn();
        if (fn) { try { fn('/pages/wishlist'); } catch (e) {} }
      }, 1000);
    }
    var retry = el('[data-hs-wishlist-retry]');
    if (retry) retry.addEventListener('click', function () { renderPage(); });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
